import { useCallback, useMemo, useState } from "react"
import Modal from "../common/modal/modal"
import { Device } from "@/lib/ts/device"
import { Stage } from "@/graphql/types"
import GuestInput from "../common/input/guestInput"
import DateTimePicker from "react-datetime-picker"
import dayjs from "dayjs"
import styled from "styled-components"
import { InputLabel } from "@mui/material"
import Switcher from "../common/switcher/switcher"
import Button from "../common/button/button"

type Props = {
    onClose: () => void
    device: Device
    stage: Stage
    updateStageSettingCallback: (stage: Stage) => Promise<void>
}

const errorMessages = {
    caution: "入力必須（記号は使えません）",
    stageName: "入力必須（記号は使えません）",
}

const validations = {
  stageName: /^[^!@#$%^&*_+{}\[\]:;<>,.?~=-]+$/,
  caution: /^[^!@#$%^&*_+{}\[\]:;<>,.?~=-]+$/,
}

const EditStage = (props: Props) => {

    const [reservationStart, setReservationStart] = useState(props.stage.reservationStart)
    const [reservationLimit, setReservationLimit] = useState(props.stage.reservationLimit)

    const [openDateTime, setOpenDateTime] = useState(props.stage.openDateTime)

    const [startDateTime, setStartDateTime] = useState(props.stage.startDateTime)

    const [endDateTime, setEndDateTime] = useState(props.stage.endDateTime)

    const [caution, setCaution] = useState(props.stage.caution)
    const [cautionError, setCautionError] = useState(false)

    const [stageName, setStageName] = useState(props.stage.stageName)
    const [stageNameError, setStageNameError] = useState(false)
    
    const [disableSeatSelect, setDisableSeatSelect] = useState(props.stage.disableSeatSelect)

    const isMobile = useCallback(() => {
        return props.device === 'Android' || props.device === 'Mobile' || props.device === 'ios'
    }, [props.device])


    const reservationStartInput = useMemo(() => (
        <div className="input">
            <InputLabel>予約開始日時</InputLabel>
            <DateTimePicker 
                className="dateTimePicker"
                disableClock
                hourHandLength={2}
                value={dayjs.unix(reservationStart).toDate()}
                maxDate={dayjs.unix(reservationLimit).toDate()}
                onChange={(value) => { setReservationStart(dayjs(value).unix()) }}
            />
        </div>
    ), [reservationLimit, reservationStart])


    const reservationLimitInput = useMemo(() => (
        <div className="input">
            <InputLabel>予約終了日時</InputLabel>
            <DateTimePicker 
                className="dateTimePicker"
                disableClock
                hourHandLength={2}
                value={dayjs.unix(reservationLimit).toDate()}
                minDate={dayjs.unix(reservationStart).toDate()}
                onChange={(value) => { setReservationLimit(dayjs(value).unix()) }}
            />
        </div>
    ), [reservationLimit, reservationStart])
    
    const openDateTimeInput = useMemo(() => (
        <div className="input">
            <InputLabel>開場日時</InputLabel>
            <DateTimePicker 
                className="dateTimePicker"
                disableClock
                hourHandLength={2}
                value={dayjs.unix(openDateTime).toDate()}
                maxDate={dayjs.unix(startDateTime).toDate()}
                onChange={(value) => { setOpenDateTime(dayjs(value).unix()) }}
            />
        </div>
    ), [openDateTime, startDateTime])


    const startDateTimeInput = useMemo(() => (
        <div className="input">
            <InputLabel>開演日時</InputLabel>
            <DateTimePicker 
                className="dateTimePicker"
                disableClock
                hourHandLength={2}
                value={dayjs.unix(startDateTime).toDate()}
                minDate={dayjs.unix(openDateTime).toDate()}
                maxDate={dayjs.unix(endDateTime).toDate()}
                onChange={(value) => { setStartDateTime(dayjs(value).unix()) }}
            />
        </div>
    ), [endDateTime, openDateTime, startDateTime])

    const endDateTimeInput = useMemo(() => (
        <div className="input">
            <InputLabel>終演日時</InputLabel>
            <DateTimePicker 
                className="dateTimePicker"
                disableClock
                hourHandLength={2}
                value={dayjs.unix(endDateTime).toDate()}
                minDate={dayjs.unix(startDateTime).toDate()}
                onChange={(value) => { setEndDateTime(dayjs(value).unix()) }}
            />
        </div>
    ), [endDateTime, startDateTime])

    const cautionInput = useMemo(() => (
        <div className="input">
            <GuestInput 
                id="caution"
                defaultValue={caution}
                error={cautionError}
                label="注意事項"
                errorMessage={errorMessages.caution}
                onChange={(value, errorOccurs) => {
                    setCautionError(errorOccurs)
                    setCaution(value)
                }}
                literalValidation={validations.caution}
                placeholder="注意事項"
                multiLine
                outlined
                width={isMobile() ? 280 : 400}
            />
        </div>
    ), [caution, cautionError, isMobile])


    const stageNameInput = useMemo(() => (
        <div className="input">
            <GuestInput 
                id="caution"
                defaultValue={stageName}
                error={stageNameError}
                label="ステージ名"
                errorMessage={errorMessages.stageName}
                onChange={(value, errorOccurs) => {
                    setStageNameError(errorOccurs)
                    setStageName(value)
                }}
                literalValidation={validations.stageName}
                placeholder="ステージ名"
                outlined
                width={isMobile() ? 280 : 400}
            />
        </div>
    ), [isMobile, stageName, stageNameError])

    const disableSeatSelectInput = useMemo(() => {
        return (
            <div className="input">
                <InputLabel>運営が座席を割り振る</InputLabel>
                <Switcher 
                    baseColor="black"
                    labelOff="OFF"
                    labelOn="ON"
                    setCallback={setDisableSeatSelect}
                    defaultValue={props.stage.disableSeatSelect || false}
                />
            </div>
        )
    }, [props.stage.disableSeatSelect])

    const save = useCallback(async () => {
        await props.updateStageSettingCallback({
            id: props.stage.id,
            caution,
            endDateTime,
            startDateTime,
            openDateTime,
            reservationLimit,
            reservationStart,
            productionId: props.stage.productionId,
            stageName,
            capacity: props.stage.capacity,
            disableSeatSelect,
        })
        props.onClose()
    }, [caution, disableSeatSelect, endDateTime, openDateTime, props, reservationLimit, reservationStart, stageName, startDateTime])

    const invalid = useMemo(() => {
        return cautionError || stageNameError
    }, [cautionError, stageNameError])

    const inputs = useMemo(() => {
        return (
            <>  
                {stageNameInput}
                {reservationStartInput}
                {reservationLimitInput}
                {openDateTimeInput}
                {startDateTimeInput}
                {endDateTimeInput}
                {cautionInput}
                {disableSeatSelectInput}
                <div>
                    <Button 
                        label="保存"
                        onClick={save}
                        buttonType="powerful-red"
                        disabled={invalid}
                    />
                </div>
            </>
        )
    }, [cautionInput, disableSeatSelectInput, endDateTimeInput, invalid, openDateTimeInput, reservationLimitInput, reservationStartInput, save, stageNameInput, startDateTimeInput])

    const component = useMemo(() => {
        return (
            <Modal
                onClose={props.onClose}
                title="ステージ情報編集"
                width={isMobile() ? 320 : 1000}
            >
                {
                    isMobile() ? (
                        <EditStageStyleMobile>
                            {inputs}
                        </EditStageStyleMobile>
                    ) : (
                        <EditStageStylePC>
                            {inputs}
                        </EditStageStylePC>
                    )
                }
            </Modal>
        )
    }, [inputs, isMobile, props.onClose])

    return component
}

const EditStageStylePC = styled.div`
    .input {
        width: 420px;
        text-align: center;
        margin: auto;
        margin: 10px auto 10px auto;
        .dateTimePicker {
            text-align: center;
            width: 400px;
        }
    }
`

const EditStageStyleMobile = styled.div`
    .input {
        width: 300px;
        text-align: center;
        margin: 10px auto 10px auto;
        .dateTimePicker {
            width: 280px;
        }
    }
`

export default EditStage